import { actionOptions, isMediaAction } from './inspectorOptions'
import type { FlowNode, GroupOption, NodeType } from './types'

type Translate = (key: string) => string

const TYPE_LABEL_KEYS: Partial<Record<NodeType, string>> = {
  start: 'flow.nodeType.start',
  phase: 'flow.nodeType.phase',
  group: 'flow.nodeType.group',
  condition: 'flow.nodeType.condition',
  action: 'flow.nodeType.action',
  'macro-start': 'flow.nodeType.macroStart',
  'macro-end': 'flow.nodeType.macroEnd',
  'macro-call': 'flow.nodeType.macroCall',
}

export const nodeTypeLabel = (t: Translate, type: NodeType) =>
  t(TYPE_LABEL_KEYS[type] ?? 'flow.nodeType.end')

function actionSummary(t: Translate, node: FlowNode) {
  const data = node.data
  const actionType = data.actionType || ''
  if (actionType === 'get-value') {
    const phase = data.getSource?.startsWith('phase-') ? ` (${data.getPhase || 'own phase'})` : ''
    return `${data.getRegister ?? 'R1'} ← ${data.getSource ?? '?'}${phase}`
  }
  if (actionType === 'set-value') return `${data.setTarget ?? '?'} ← ${data.setInput ?? 'R1'}`
  if (actionType === 'math-op') {
    const op = data.mathOp ?? '+1'
    const operand =
      op === '+1' || op === '-1'
        ? ''
        : ` ${data.mathOperand === 'literal' ? String(data.mathLit ?? 0) : (data.mathOperand ?? 'R1')}`
    return `${data.mathTarget ?? 'R1'} ${op}${operand}`
  }
  if (isMediaAction(actionType)) {
    const mediaType =
      data.mediaType ?? (actionType !== 'show-media' ? actionType.slice(5) : 'image')
    return node.label ? `${mediaType}: "${node.label}"` : actionType
  }
  const found = actionOptions.find((item) => item.value === actionType)
  return found?.label || actionType || t('flow.summary.noAction')
}

/** One-line description shown in the node card body and in SVG exports. */
export function nodeSummary(t: Translate, node: FlowNode, groups: GroupOption[]): string {
  const data = node.data
  switch (node.type) {
    case 'group': {
      if (data.groupId === '__all__') return t('flow.summary.allCards')
      const group = groups.find((g) => g.id === data.groupId)
      return group
        ? `${t('flow.summary.group')} ${group.title}`
        : data.groupId || t('flow.summary.noGroupSelected')
    }
    case 'condition': {
      const left = data.condLeft ?? 'R1'
      const right = data.condRight ?? 'literal'
      const leftText = left === 'literal' ? String(data.condLeftLit ?? 0) : left
      const rightText = right === 'literal' ? String(data.condRightLit ?? 0) : right
      return `${leftText} ${data.condOp ?? '=='} ${rightText}`
    }
    case 'phase':
      return data.phaseId
        ? `${t('flow.summary.phase')}: ${data.phaseId}`
        : t('flow.summary.noPhaseId')
    case 'action':
      return actionSummary(t, node)
    case 'start':
      return t('flow.summary.entryPoint')
    case 'macro-start':
      return data.macroName
        ? `"${data.macroName}" · ${data.inputCount ?? 1} ${t('flow.summary.in')}`
        : t('flow.summary.unnamed')
    case 'macro-end':
      return `→ "${data.macroRef || '?'}" · ${data.outputCount ?? 1} ${t('flow.summary.out')}`
    case 'macro-call':
      return data.macroRef
        ? `${t('flow.summary.call')} "${data.macroRef}"`
        : t('flow.summary.noMacroSelected')
    default:
      return t('flow.summary.terminal')
  }
}

/**
 * Text on an output handle: macro ports show their index, the default 'next' port
 * stays unlabeled, other ports (e.g. condition true/else) show their name.
 */
export const outputPortLabel = (node: FlowNode, port: string, index: number) => {
  if (node.type === 'macro-start' || node.type === 'macro-end' || node.type === 'macro-call') {
    return index
  }
  return port === 'next' ? '' : port
}
